import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Pencil, Plus, Printer, Settings2, Sparkles, Trash2, Upload } from 'lucide-react';
import {
  PAYMENT_METHODS,
  PURCHASE_CATEGORIES,
  PURCHASE_EVIDENCE_KINDS,
  PURCHASE_LEDGER_COPY,
  paymentMethodLabel,
  purchaseCategoryLabel,
  purchaseEvidenceKindLabel,
  type PurchaseExtractDraft,
  type PurchaseLedgerTemplate,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { todayISO } from '@/lib/format';
import { uploadEvidence } from '@/lib/upload';
import { useCanWrite } from '@/store/auth';

interface PurchaseEntry {
  id: string;
  purchasedOn: string;
  category: string;
  itemName: string;
  spec: string | null;
  quantity: number | null;
  unitPrice: number | null;
  amount: number;
  vendor: string | null;
  paymentMethod: string | null;
  purpose: string | null;
  evidenceKind: string | null;
  mediaId: string | null;
  evidenceMimeType: string | null;
  aiExtracted: boolean;
  recorderName: string;
  canEdit: boolean;
}

interface LedgerPeriod {
  period: string;
  label: string;
  count: number;
  total: number;
  byCategory: { category: string; amount: number; count: number }[];
  entries: PurchaseEntry[];
}

interface TemplateInfo {
  template: PurchaseLedgerTemplate;
  canEditTemplate: boolean;
  aiAvailable: boolean;
  aiDailyLimit: number;
  aiUsedToday: number;
}

/** 편집 폼 값 — 숫자도 입력 중에는 문자열로 둔다 */
interface FormValues {
  purchasedOn: string;
  category: string;
  itemName: string;
  spec: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  vendor: string;
  paymentMethod: string;
  purpose: string;
  evidenceKind: string;
}

type DraftItem = PurchaseExtractDraft['items'][number];

interface EditorState {
  mode: 'create' | 'edit';
  id?: string;
  values: FormValues;
  mediaId: string | null;
  /** AI가 읽은 초안인지 — 저장 시 aiExtracted 로 남긴다 */
  fromAi: boolean;
  items: DraftItem[];
  confidence: number | null;
  notes: string[];
  aiUnavailable: boolean;
}

const emptyValues = (): FormValues => ({
  purchasedOn: todayISO(),
  category: 'SUPPLIES',
  itemName: '',
  spec: '',
  quantity: '',
  unitPrice: '',
  amount: '',
  vendor: '',
  paymentMethod: 'CARD',
  purpose: '',
  evidenceKind: 'RECEIPT',
});

const won = (value: number) => `${value.toLocaleString('ko-KR')}원`;
const digits = (value: string): number | null => {
  const cleaned = value.replace(/[^\d]/g, '');
  return cleaned ? Number(cleaned) : null;
};

function valuesFromEntry(entry: PurchaseEntry): FormValues {
  return {
    purchasedOn: entry.purchasedOn,
    category: entry.category,
    itemName: entry.itemName,
    spec: entry.spec ?? '',
    quantity: entry.quantity == null ? '' : String(entry.quantity),
    unitPrice: entry.unitPrice == null ? '' : String(entry.unitPrice),
    amount: String(entry.amount),
    vendor: entry.vendor ?? '',
    paymentMethod: entry.paymentMethod ?? '',
    purpose: entry.purpose ?? '',
    evidenceKind: entry.evidenceKind ?? '',
  };
}

function valuesFromDraft(draft: PurchaseExtractDraft): FormValues {
  const base = emptyValues();
  return {
    purchasedOn: draft.purchasedOn ?? base.purchasedOn,
    category: draft.category ?? base.category,
    itemName: draft.itemName ?? '',
    spec: draft.spec ?? '',
    quantity: draft.quantity == null ? '' : String(draft.quantity),
    unitPrice: draft.unitPrice == null ? '' : String(draft.unitPrice),
    amount: draft.amount == null ? '' : String(draft.amount),
    vendor: draft.vendor ?? '',
    paymentMethod: draft.paymentMethod ?? base.paymentMethod,
    purpose: draft.purpose ?? '',
    evidenceKind: draft.evidenceKind ?? base.evidenceKind,
  };
}

/**
 * 구매대장 — 명세서 §5.29 (v1.20)
 *
 * 증빙(영수증·견적서·주문 캡처)을 올리면 AI가 항목을 읽어 초안을 채우고, 교직원이
 * 확인해 저장하면 대장 한 줄이 된다. 월별 대장 PDF는 원 양식(원장이 설정)으로 그린다.
 */
export function PurchasesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const fileRef = useRef<HTMLInputElement>(null);
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busy, setBusy] = useState<'upload' | 'extract' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery({
    queryKey: ['purchases', 'template'],
    queryFn: () => apiData<TemplateInfo>('/purchases/template'),
  });

  const ledger = useQuery({
    queryKey: ['purchases', 'month', month],
    queryFn: () => apiData<LedgerPeriod>(`/purchases?month=${month}`),
    placeholderData: keepPreviousData,
  });

  const evidenceIds = (ledger.data?.entries ?? [])
    .map((entry) => entry.mediaId)
    .filter((id): id is string => Boolean(id));
  const evidenceUrls = useQuery({
    queryKey: ['media', 'urls', evidenceIds.join(',')],
    queryFn: () =>
      apiData<{ urls: { id: string; url: string }[] }>(`/media/urls?ids=${evidenceIds.join(',')}`),
    enabled: evidenceIds.length > 0,
    staleTime: 10 * 60_000,
  });
  const urlById = new Map((evidenceUrls.data?.urls ?? []).map((row) => [row.id, row.url]));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['purchases'] });
  };

  const shiftMonth = (delta: number) => {
    const [y, m] = month.split('-').map(Number);
    setMonth(new Date(Date.UTC(y, m - 1 + delta, 1)).toISOString().slice(0, 7));
  };

  /** 증빙 파일 → 업로드 → (AI가 켜져 있으면) 읽기 → 편집기 열기 */
  const handleFile = async (file: File) => {
    setError(null);
    setBusy('upload');
    let mediaId: string | null = null;
    try {
      mediaId = (await uploadEvidence(file)).mediaId;
      if (!info.data?.aiAvailable) {
        setEditor({
          mode: 'create',
          values: emptyValues(),
          mediaId,
          fromAi: false,
          items: [],
          confidence: null,
          notes: [],
          aiUnavailable: true,
        });
        return;
      }
      setBusy('extract');
      const result = await apiData<{ draft: PurchaseExtractDraft }>('/purchases/extract', {
        method: 'POST',
        body: { mediaId },
      });
      setEditor({
        mode: 'create',
        values: valuesFromDraft(result.draft),
        mediaId,
        fromAi: true,
        items: result.draft.items,
        confidence: result.draft.confidence,
        notes: result.draft.notes,
        aiUnavailable: false,
      });
      void queryClient.invalidateQueries({ queryKey: ['purchases', 'template'] });
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : '증빙을 올리지 못했어요.';
      // 읽기에 실패해도 첨부는 살려 두고 직접 입력으로 이어 간다
      if (mediaId) {
        setEditor({
          mode: 'create',
          values: emptyValues(),
          mediaId,
          fromAi: false,
          items: [],
          confidence: null,
          notes: [],
          aiUnavailable: false,
        });
      }
      setError(message);
    } finally {
      setBusy(null);
    }
  };

  const remove = useMutation({
    mutationFn: (id: string) => apiData(`/purchases/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '지우지 못했어요.'),
  });

  const data = ledger.data;

  return (
    <Screen>
      <AppBar
        title={PURCHASE_LEDGER_COPY.menu}
        back
        right={
          info.data?.canEditTemplate ? (
            <button
              type="button"
              aria-label="양식 설정"
              className="tap grid place-items-center rounded-full text-ink hover:bg-primary-soft"
              onClick={() => navigate('/purchases/template')}
            >
              <Settings2 size={20} />
            </button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-ink-sub">{PURCHASE_LEDGER_COPY.intro}</p>

        <div className="flex items-center justify-between">
          <Button size="sm" variant="ghost" onClick={() => shiftMonth(-1)}>
            ← 지난달
          </Button>
          <p className="font-semibold">{data?.label ?? month.replace('-', '년 ') + '월'}</p>
          <Button size="sm" variant="ghost" onClick={() => shiftMonth(1)}>
            다음달 →
          </Button>
        </div>

        {data && (
          <Card>
            <p className="text-sm text-ink-sub">이달 구매 합계</p>
            <p className="tnum mt-0.5 text-2xl font-bold">{won(data.total)}</p>
            <p className="mt-0.5 text-sm text-ink-sub">
              {data.count}건
              {data.byCategory.length > 0 && (
                <>
                  {' · '}
                  {data.byCategory
                    .slice(0, 3)
                    .map((row) => `${purchaseCategoryLabel(row.category)} ${won(row.amount)}`)
                    .join(' · ')}
                </>
              )}
            </p>
          </Card>
        )}

        {canWrite && !editor && (
          <div className="flex gap-2">
            <Button
              block
              icon={<Upload size={16} />}
              loading={busy !== null}
              onClick={() => fileRef.current?.click()}
            >
              {busy === 'upload' ? '증빙 올리는 중' : busy === 'extract' ? '증빙 읽는 중' : '증빙으로 추가'}
            </Button>
            <Button
              block
              variant="outline"
              icon={<Plus size={16} />}
              disabled={busy !== null}
              onClick={() =>
                setEditor({
                  mode: 'create',
                  values: emptyValues(),
                  mediaId: null,
                  fromAi: false,
                  items: [],
                  confidence: null,
                  notes: [],
                  aiUnavailable: false,
                })
              }
            >
              직접 입력
            </Button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
        {info.data && !info.data.aiAvailable && canWrite && !editor && (
          <p className="text-sm text-ink-sub">{PURCHASE_LEDGER_COPY.aiUnavailableHint}</p>
        )}
        {info.data?.aiAvailable && !editor && (
          <p className="text-sm text-ink-sub">
            오늘 증빙 읽기 {info.data.aiUsedToday}/{info.data.aiDailyLimit}회
          </p>
        )}

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        {editor && (
          <EntryEditor
            editor={editor}
            onChange={setEditor}
            onClose={() => setEditor(null)}
            onSaved={() => {
              setEditor(null);
              setError(null);
              refresh();
            }}
          />
        )}

        {ledger.isPending && <Spinner />}
        {ledger.isError && (
          <ErrorNote message="구매대장을 불러오지 못했어요." onRetry={() => void ledger.refetch()} />
        )}

        {data && data.entries.length === 0 && !editor && (
          <EmptyState title={PURCHASE_LEDGER_COPY.emptyTitle} body={PURCHASE_LEDGER_COPY.emptyBody} />
        )}

        {data?.entries.map((entry) => (
          <Card key={entry.id}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{entry.itemName}</p>
                <p className="text-sm text-ink-sub">
                  {entry.purchasedOn}
                  {entry.vendor ? ` · ${entry.vendor}` : ''}
                  {entry.paymentMethod ? ` · ${paymentMethodLabel(entry.paymentMethod)}` : ''}
                </p>
                {entry.purpose && <p className="mt-0.5 text-sm text-ink-sub">{entry.purpose}</p>}
              </div>
              <p className="tnum shrink-0 font-bold">{won(entry.amount)}</p>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Chip tone="primary">{purchaseCategoryLabel(entry.category)}</Chip>
              {entry.evidenceKind && <Chip>{purchaseEvidenceKindLabel(entry.evidenceKind)}</Chip>}
              {entry.aiExtracted && (
                <Chip tone="info">
                  <Sparkles size={12} /> AI 읽음
                </Chip>
              )}
              {entry.mediaId && urlById.get(entry.mediaId) && (
                <a
                  href={urlById.get(entry.mediaId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-bg px-3 py-1.5 text-sm font-medium text-primary-strong"
                >
                  <Paperclip size={12} /> 증빙 보기
                </a>
              )}
              {entry.canEdit && canWrite && (
                <span className="ml-auto flex items-center">
                  <button
                    type="button"
                    aria-label="수정"
                    className="tap grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-primary-strong"
                    onClick={() =>
                      setEditor({
                        mode: 'edit',
                        id: entry.id,
                        values: valuesFromEntry(entry),
                        mediaId: entry.mediaId,
                        fromAi: entry.aiExtracted,
                        items: [],
                        confidence: null,
                        notes: [],
                        aiUnavailable: false,
                      })
                    }
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="삭제"
                    className="tap grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-notice"
                    onClick={() => {
                      if (window.confirm(`${entry.itemName} 항목을 지울까요?`)) remove.mutate(entry.id);
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              )}
            </div>
          </Card>
        ))}

        {data && (
          <div className="flex flex-col gap-2 pt-2">
            <LedgerPdfButton label={`${data.label} 구매대장 PDF`} body={{ month }} />
            <LedgerPdfButton label={`${month.slice(0, 4)}년 전체 구매대장 PDF`} body={{ year: Number(month.slice(0, 4)) }} />
            <p className="text-sm text-ink-sub">
              {info.data
                ? `양식: ${info.data.template.title} · ${info.data.template.columns.length}열${
                    info.data.template.signatures.length ? ` · 결재란 ${info.data.template.signatures.join('/')}` : ''
                  }`
                : ''}
            </p>
          </div>
        )}
      </div>
    </Screen>
  );
}

/** 대장 PDF 버튼 — 만들고 나면 열기 링크로 바뀐다 (서류함 병합과 같은 방식) */
function LedgerPdfButton({ label, body }: { label: string; body: { month?: string; year?: number } }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const make = useMutation({
    mutationFn: () => apiData<{ url: string }>('/purchases/ledger-pdf', { method: 'POST', body }),
    onSuccess: (result) => {
      setUrl(result.url);
      setError(null);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '만들지 못했어요.'),
  });

  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <Button block variant="outline" icon={<Printer size={16} />}>
          {label} 열기
        </Button>
      </a>
    );
  }

  return (
    <div>
      <Button block variant="secondary" icon={<Printer size={16} />} loading={make.isPending} onClick={() => make.mutate()}>
        {label}
      </Button>
      {error && <p className="mt-1 text-sm text-notice">{error}</p>}
    </div>
  );
}

function EntryEditor({
  editor,
  onChange,
  onClose,
  onSaved,
}: {
  editor: EditorState;
  onChange: (next: EditorState) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [showItems, setShowItems] = useState(false);
  const values = editor.values;

  const set = (patch: Partial<FormValues>) => {
    const next = { ...values, ...patch };
    // 수량·단가를 바꿀 때 금액이 비어 있거나 직전 곱과 같으면 곱해서 채운다.
    // 사람이 따로 적은 금액(할인 등)은 건드리지 않는다 — 확인은 사람이 한다.
    if ('quantity' in patch || 'unitPrice' in patch) {
      const quantity = digits(next.quantity);
      const unitPrice = digits(next.unitPrice);
      const prevQuantity = digits(values.quantity);
      const prevUnitPrice = digits(values.unitPrice);
      const prevProduct =
        prevQuantity !== null && prevUnitPrice !== null ? prevQuantity * prevUnitPrice : null;
      const untouched = !next.amount || (prevProduct !== null && digits(values.amount) === prevProduct);
      if (quantity !== null && unitPrice !== null && untouched) {
        next.amount = String(quantity * unitPrice);
      }
    }
    onChange({ ...editor, values: next });
  };

  const payload = () => ({
    purchasedOn: values.purchasedOn,
    category: values.category,
    itemName: values.itemName.trim(),
    spec: values.spec.trim() || null,
    quantity: digits(values.quantity),
    unitPrice: digits(values.unitPrice),
    amount: digits(values.amount) ?? 0,
    vendor: values.vendor.trim() || null,
    paymentMethod: values.paymentMethod || null,
    purpose: values.purpose.trim() || null,
    evidenceKind: values.evidenceKind || null,
    mediaId: editor.mediaId,
  });

  const validate = (): string | null => {
    if (!values.purchasedOn) return '구매일을 골라 주세요.';
    if (!values.itemName.trim()) return '품목을 적어 주세요.';
    if (digits(values.amount) === null) return '금액을 적어 주세요.';
    return null;
  };

  const save = useMutation({
    mutationFn: () => {
      const body = { ...payload(), ...(editor.mode === 'create' ? { aiExtracted: editor.fromAi } : {}) };
      return editor.mode === 'edit'
        ? apiData(`/purchases/${editor.id}`, { method: 'PATCH', body })
        : apiData('/purchases', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  /** 읽은 품목 줄을 각각 한 행으로 — 날짜·거래처·결제·증빙은 공통 */
  const saveSplit = useMutation({
    mutationFn: () => {
      const base = payload();
      return apiData('/purchases/batch', {
        method: 'POST',
        body: {
          entries: editor.items.map((item) => ({
            ...base,
            itemName: item.itemName,
            spec: item.spec,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            amount:
              item.amount ??
              (item.quantity != null && item.unitPrice != null ? item.quantity * item.unitPrice : 0),
            aiExtracted: true,
          })),
        },
      });
    },
    onSuccess: onSaved,
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  const submit = (split: boolean) => {
    const problem = split ? null : validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    if (split) saveSplit.mutate();
    else save.mutate();
  };

  const lowConfidence = editor.confidence !== null && editor.confidence < 0.6;

  return (
    <Card>
      <p className="mb-2 font-semibold">{editor.mode === 'edit' ? '항목 수정' : '구매 항목 추가'}</p>

      {editor.fromAi && editor.mode === 'create' && (
        <div className="mb-3 rounded-sm bg-info-soft px-3 py-2 text-sm">
          <p className="flex items-start gap-1.5">
            <Sparkles size={14} className="mt-0.5 shrink-0" />
            <span>
              {PURCHASE_LEDGER_COPY.aiDraftNotice}
              {lowConfidence ? ' 이번 증빙은 읽기 확신도가 낮아 원본과 꼭 대조해 주세요.' : ''}
            </span>
          </p>
          {editor.notes.length > 0 && (
            <ul className="mt-1.5 list-disc pl-5 text-ink-sub">
              {editor.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {editor.aiUnavailable && (
        <p className="mb-3 rounded-sm bg-accent-soft px-3 py-2 text-sm">{PURCHASE_LEDGER_COPY.aiUnavailable}</p>
      )}
      {editor.mediaId && (
        <p className="mb-3 flex items-center gap-1.5 text-sm text-primary-strong">
          <Paperclip size={14} /> 증빙 파일이 함께 저장돼요
        </p>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Field label="구매일" required>
            <Input type="date" value={values.purchasedOn} onChange={(e) => set({ purchasedOn: e.target.value })} />
          </Field>
          <Field label="금액(원)" required>
            <Input
              inputMode="numeric"
              placeholder="예: 45000"
              value={values.amount}
              onChange={(e) => set({ amount: e.target.value })}
            />
          </Field>
        </div>

        <Field label="품목" required>
          <Input placeholder="예: 색종이 외 3건" value={values.itemName} onChange={(e) => set({ itemName: e.target.value })} />
        </Field>

        <FieldGroup label="분류">
          <div className="flex flex-wrap gap-1.5">
            {PURCHASE_CATEGORIES.map((category) => (
              <Chip
                key={category.code}
                tone={values.category === category.code ? 'primary' : 'neutral'}
                selected={values.category === category.code}
                onClick={() => set({ category: category.code })}
              >
                {category.label}
              </Chip>
            ))}
          </div>
        </FieldGroup>

        <div className="flex gap-2">
          <Field label="수량">
            <Input inputMode="numeric" value={values.quantity} onChange={(e) => set({ quantity: e.target.value })} />
          </Field>
          <Field label="단가(원)">
            <Input inputMode="numeric" value={values.unitPrice} onChange={(e) => set({ unitPrice: e.target.value })} />
          </Field>
          <Field label="규격">
            <Input placeholder="500매" value={values.spec} onChange={(e) => set({ spec: e.target.value })} />
          </Field>
        </div>

        <Field label="거래처">
          <Input placeholder="예: 든든문구" value={values.vendor} onChange={(e) => set({ vendor: e.target.value })} />
        </Field>

        <FieldGroup label="결제 방법">
          <div className="flex flex-wrap gap-1.5">
            {PAYMENT_METHODS.map((method) => (
              <Chip
                key={method.code}
                tone={values.paymentMethod === method.code ? 'primary' : 'neutral'}
                selected={values.paymentMethod === method.code}
                onClick={() => set({ paymentMethod: values.paymentMethod === method.code ? '' : method.code })}
              >
                {method.label}
              </Chip>
            ))}
          </div>
        </FieldGroup>

        <FieldGroup label="증빙 종류">
          <div className="flex flex-wrap gap-1.5">
            {PURCHASE_EVIDENCE_KINDS.map((kind) => (
              <Chip
                key={kind.code}
                tone={values.evidenceKind === kind.code ? 'primary' : 'neutral'}
                selected={values.evidenceKind === kind.code}
                onClick={() => set({ evidenceKind: values.evidenceKind === kind.code ? '' : kind.code })}
              >
                {kind.label}
              </Chip>
            ))}
          </div>
        </FieldGroup>

        <Field label="용도·비고">
          <Textarea rows={2} placeholder="예: 햇살반 가을 미술 활동" value={values.purpose} onChange={(e) => set({ purpose: e.target.value })} />
        </Field>

        {editor.items.length > 1 && (
          <div className="rounded-sm bg-bg px-3 py-2 text-sm">
            <button type="button" className="font-medium text-primary-strong" onClick={() => setShowItems((v) => !v)}>
              증빙에서 읽은 품목 {editor.items.length}줄 {showItems ? '접기' : '펼치기'}
            </button>
            {showItems && (
              <ul className="mt-1.5 flex flex-col gap-1 text-ink-sub">
                {editor.items.map((item, index) => (
                  <li key={`${item.itemName}-${index}`} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {item.itemName}
                      {item.quantity != null ? ` ×${item.quantity}` : ''}
                    </span>
                    <span className="tnum shrink-0">{item.amount != null ? won(item.amount) : '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button block loading={save.isPending} disabled={saveSplit.isPending} onClick={() => submit(false)}>
            {editor.mode === 'edit' ? '수정 저장' : editor.items.length > 1 ? '1건으로 저장' : '저장'}
          </Button>
          {editor.mode === 'create' && editor.items.length > 1 && (
            <Button block variant="secondary" loading={saveSplit.isPending} disabled={save.isPending} onClick={() => submit(true)}>
              품목별 {editor.items.length}건으로 저장
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </Card>
  );
}
